import React, { useState, useCallback, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  AppBar,
  Toolbar,
  IconButton,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Lock } from '@mui/icons-material';
import { AppHeader } from '../components/AppHeader';
import { useSnackbar } from '../contexts/SnackbarContext';
import api from '../services/api';

interface FormData {
  cpfOrEmail: string;
  newPassword: string;
  confirmPassword: string;
}

const ResetUserPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue
  } = useForm<FormData>();

  // Pré-preenche o CPF quando chega da tela de listagem de usuários
  // (ex: /reset-user-password?cpf=123.456.789-00)
  useEffect(() => {
    const cpf = searchParams.get('cpf');
    if (cpf) {
      setValue('cpfOrEmail', cpf);
    }
  }, [searchParams, setValue]);

  // Funções auxiliares para usar o snackbar
  const showSuccess = useCallback((message: string) => showSnackbar(message, 'success'), [showSnackbar]);
  const showError = useCallback((message: string) => showSnackbar(message, 'error'), [showSnackbar]);

  // Função para determinar rota de volta baseada no role
  const getBackRoute = () => {
    switch (user?.role) {
      case 'SUPER_ADMIN':
        return '/dashboard';
      case 'ASSOCIACAO':
        return '/dashboard';
      case 'USUARIO_FINAL':
        return '/campaigns';
      default:
        return '/dashboard';
    }
  };

  // Verificar se é ASSOCIACAO
  React.useEffect(() => {
    if (!user || user.role !== 'ASSOCIACAO') {
      navigate(getBackRoute());
    }
  }, [user, navigate]);

  const validatePassword = (value: string) => {
    if (value.length < 6) {
      return 'Senha deve ter no mínimo 6 caracteres';
    }
    return true;
  };

  const validateConfirmPassword = (value: string) => {
    const password = watch('newPassword');
    if (value !== password) {
      return 'Confirmação de senha não confere';
    }
    return true;
  };

  const onSubmit = async (data: FormData) => {
    try {
      setLoading(true);
      
      const response = await api.post('/auth/admin/reset-password', {
        cpfOrEmail: data.cpfOrEmail.trim(),
        newPassword: data.newPassword,
      });

      showSuccess(`Senha redefinida com sucesso para ${response.data.user.name} (${response.data.user.email})`);
      reset();
      
    } catch (error: any) {
      console.error('Erro ao redefinir senha:', error);
      
      if (error.response?.data?.message) {
        showError(error.response.data.message);
      } else if (error.response?.status === 404) {
        showError('Usuário não encontrado com este CPF ou email');
      } else if (error.response?.status === 409) {
        showError('Não é permitido redefinir senha de outros usuários ASSOCIACAO');
      } else if (error.response?.status === 400) {
        showError('Nova senha não atende aos critérios de segurança');
      } else {
        showError('Erro interno do servidor. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      <AppHeader title="Redefinir Senha do Usuário" subtitle="Redefina a senha de um usuário específico" icon={<Lock />} backTo={getBackRoute()} />

      <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" align="center" gutterBottom>
            Redefinir Senha de Usuário
          </Typography>
          
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Digite o CPF ou email do usuário para redefinir sua senha
          </Typography>

          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 3 }}>
            <TextField
              {...register('cpfOrEmail', { 
                required: 'CPF ou email é obrigatório',
                validate: (value) => {
                  const trimmed = value.trim();
                  if (trimmed.length === 0) {
                    return 'CPF ou email é obrigatório';
                  }
                  return true;
                }
              })}
              margin="normal"
              required
              fullWidth
              label="CPF ou Email do Usuário"
              placeholder="123.456.789-00 ou usuario@email.com"
              error={!!errors.cpfOrEmail}
              helperText={errors.cpfOrEmail?.message}
              disabled={loading}
            />

            <TextField
              {...register('newPassword', { validate: validatePassword })}
              margin="normal"
              required
              fullWidth
              label="Nova Senha"
              type="password"
              error={!!errors.newPassword}
              helperText={errors.newPassword?.message}
              disabled={loading}
            />

            <TextField
              {...register('confirmPassword', { validate: validateConfirmPassword })}
              margin="normal"
              required
              fullWidth
              label="Confirmar Nova Senha"
              type="password"
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              disabled={loading}
            />

            <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
              <Typography variant="body2">
                <strong>Critério da senha:</strong> mínimo 6 caracteres
              </Typography>
            </Alert>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} color="inherit" />
                  <span>Redefinindo...</span>
                </Box>
              ) : (
                'Redefinir Senha'
              )}
            </Button>

            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate(getBackRoute())}
              disabled={loading}
            >
              Voltar ao Dashboard
            </Button>
          </Box>
        </Paper>
      </Container>
    </>
  );
}; 
export default ResetUserPassword;